/**
 * @Author: Susana Garcia Rodriguez
 * @JobTitle: Frontend
 * @Email: sgarcia@ibl.mx
 */
import { useTranslation } from 'react-i18next';
import { CustomCardGrid } from '../../../../../components/CustomCardGrid';
import { status, STATUS_MODEL } from '../../../../../enums/statusEnum';
import { statusBorderColor, statusColor, statusFontColor } from '../../../../../enums/statusColorEnum';
import { gender } from '../../../../../enums/genderEnum';
import { EMPTY_VALUES } from '../../../../../Apis/functions';
import CustomChip from '../../../../../components/CustomChip';
import RowCardText from '../../../../../components/RowCardText';
import {
    EmailSubsectionChip,
    IconBuildingTable,
    IconGender,
    IconIdentificationCard,
    IconProfileDropDown,
    PhoneSubsectionChip,
} from '../../../../../components/buttons/IconButtons';
import userDefault from '../../../../../assets/ImagesDefaults/ic-person-v2.svg';
import CustomTooltip from '../../../../../components/CustomTooltip';

const ContractorCardGrid = ({ elementDetails }) => {
    const { t } = useTranslation();

    return (
        <>
            <CustomCardGrid
                classNamePanelGrid=''
                height='border-full-shades-200'
                itemId={elementDetails?.id}
                statusColor={statusColor[elementDetails?.statusId]}
                fontColor={statusFontColor[elementDetails?.statusId]}
                borderColor={statusBorderColor[elementDetails?.statusId]}
                statusName={t(status[elementDetails?.statusId]) || EMPTY_VALUES.DEFAULT}
                chipVariant={'large'}
                isSquareImg={false}
                isCheckboxShow={false}
                headClass={'minh-144px'}
                srcImage={elementDetails?.selfie ? `data:image/png;base64,${elementDetails?.selfie}` : userDefault}
                backgroundImage={
                    elementDetails?.selfie != null ? `data:image/png;base64,${elementDetails?.selfie}` : ''
                }
            >
                <div className='d-flex flex-column align-items-center mb-4'>
                    <CustomTooltip
                        text={`${elementDetails?.name} ${elementDetails?.lastName ?? ''} ${elementDetails?.secondLastName ?? ''}`}
                        classes={`ibl-h3 ibl-font-w-600`}
                        showIfTruncated={true}
                        tooltipClasses={'p-0'}
                    />

                    <CustomChip
                        background={statusColor[STATUS_MODEL.IN_PROGRESS]}
                        fontColor={statusFontColor[STATUS_MODEL.DELIVERED]}
                        borderColor={statusBorderColor[STATUS_MODEL.TO_APPROVE_DOCUMENT]}
                        legend={elementDetails?.acronym}
                        variant={'compact'}
                    />
                </div>
                <div className='border-bottom-shades-300 mb-3 ' />
                <div className='d-flex align-items-center my-2'>
                    <IconBuildingTable className='w-20px h-20px' />
                    <RowCardText
                        className='my-2 ml-3 d-flex justify-content-between overflow-hidden'
                        classNameValue='fs-14px ibl-font-w-300 '
                        classNameTitle='fs-12px ibl-font-w-600 '
                        title={t('company_name')}
                        value={
                            <CustomTooltip
                                text={elementDetails?.contractorCompanyName || EMPTY_VALUES.DEFAULT}
                                placement='bottom'
                                classes='text-truncate'
                            >
                                <span className='text-truncate d-inline-block'>
                                    {elementDetails?.contractorCompanyName || EMPTY_VALUES.DEFAULT}
                                </span>
                            </CustomTooltip>
                        }
                    />
                </div>
                <div className='d-flex align-items-center my-2'>
                    <IconProfileDropDown className='w-20px h-20px' />
                    <RowCardText
                        className='my-2 ml-3 d-flex justify-content-between overflow-hidden'
                        classNameValue='fs-14px ibl-font-w-300 '
                        classNameTitle='fs-12px ibl-font-w-600 '
                        title={t('manager')}
                        value={
                            <CustomTooltip
                                text={elementDetails?.name || EMPTY_VALUES.DEFAULT}
                                placement='bottom'
                                classes='text-truncate'
                            >
                                <span className='text-truncate d-inline-block'>
                                    {elementDetails?.name || EMPTY_VALUES.DEFAULT}
                                </span>
                            </CustomTooltip>
                        }
                    />
                </div>

                <div className='d-flex align-items-center my-2'>
                    <EmailSubsectionChip className='w-20px h-20px' />
                    <RowCardText
                        className='my-2 ml-3 d-flex justify-content-between overflow-hidden'
                        classNameValue='fs-14px ibl-font-w-300 '
                        classNameTitle='fs-12px ibl-font-w-600 '
                        title={t('email')}
                        value={
                            <CustomTooltip
                                text={elementDetails?.email || EMPTY_VALUES.DEFAULT}
                                placement='bottom'
                                classes='text-truncate'
                            >
                                <span className='text-truncate d-inline-block'>
                                    {elementDetails?.email || EMPTY_VALUES.DEFAULT}
                                </span>
                            </CustomTooltip>
                        }
                    />
                </div>
                <div className='d-flex align-items-center my-2'>
                    <PhoneSubsectionChip className='w-20px h-20px' />
                    <RowCardText
                        className='my-2 ml-3 d-flex justify-content-between overflow-hidden'
                        classNameValue='fs-14px ibl-font-w-300 '
                        classNameTitle='fs-12px ibl-font-w-600 '
                        title={t('telephone')}
                        value={elementDetails?.phoneNumber || EMPTY_VALUES.DEFAULT}
                    />
                </div>
                <div className='d-flex align-items-center my-2'>
                    <IconGender className='w-20px h-20px' />
                    <RowCardText
                        className='my-2 ml-3 d-flex justify-content-between overflow-hidden'
                        classNameValue='fs-14px ibl-font-w-300 '
                        classNameTitle='fs-12px ibl-font-w-600 '
                        title={t('gender')}
                        value={
                            elementDetails?.user?.genderId
                                ? t(gender[elementDetails?.user?.genderId])
                                : elementDetails?.genderId
                                  ? t(gender[elementDetails?.genderId])
                                  : EMPTY_VALUES.DEFAULT
                        }
                    />
                </div>
                <div className='d-flex align-items-center my-2'>
                    <IconIdentificationCard className='w-20px h-20px' />
                    <RowCardText
                        className='my-2 ml-3 d-flex justify-content-between overflow-hidden'
                        classNameValue='fs-14px ibl-font-w-300 '
                        classNameTitle='fs-12px ibl-font-w-600 '
                        title={t('document_id')}
                        value={
                            <CustomTooltip
                                text={elementDetails?.documentId || EMPTY_VALUES.DEFAULT}
                                placement='bottom'
                                classes='text-truncate'
                            >
                                <span className='text-truncate d-inline-block'>
                                    {elementDetails?.documentId || EMPTY_VALUES.DEFAULT}
                                </span>
                            </CustomTooltip>
                        }
                    />
                </div>
            </CustomCardGrid>
        </>
    );
};

export default ContractorCardGrid;
